import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, Spinner, Switch, Textarea } from '@nextui-org/react';
import { useNavigate } from 'react-router-dom';
import es from '../../../renderer/i18n/locales/es.json';
import { apiFetch, getApiBase } from '../lib/api';
import { LAUNCH_FONT_URL, LAUNCH_LOGO_URL, LaunchContent, LaunchIcons, LaunchView } from '../lib/agencyLaunch';
import { useAuth } from '../hooks/useAuth';

// Agencias → Lanzamiento: la pantalla que ve en la pestaña Agencias de la app
// todo el que no es ADMIN hasta que Agencias se abra (anuncio de TikControl
// Agencies con su fecha, las tarjetas de lo que traerá y «Agenda tu llamada»).
// Aquí se cambian la fecha, los textos y las tarjetas; a la derecha, la misma
// pantalla que pinta la app (lib/agencyLaunch.js importa sus archivos).
//
// Un campo vacío = su texto de fábrica (el gris del propio campo), que la app
// enseña traducido a sus 14 idiomas. Lo que se escribe aquí sale tal cual en
// español y, para el resto de idiomas, en inglés.
// Worker: cf-worker/src/api/agencyLaunch.js (D1 app_config['agency_launch']).

const ENDPOINT = '/api/backoffice/agency-launch';
const C = LaunchContent;
const L = C.LIMITS;

const LANGS = [
  { id: 'es', label: 'Español' },
  { id: 'en', label: 'English' }
];

// Hueco de la vista previa: la pantalla se adapta a la forma de la ventana.
const SHAPES = [
  { id: 'wide', label: 'Ventana ancha', ratio: '1706 / 880', hint: 'Como el diseño: con las dos pantallas de los lados.' },
  { id: 'mid', label: 'Media', ratio: '1280 / 836', hint: 'Menos apaisada: las pantallas de los lados encogen.' },
  { id: 'narrow', label: 'Estrecha', ratio: '820 / 760', hint: 'Casi cuadrada: sin las pantallas de los lados.' }
];

const TEXT_FIELDS = [
  { name: 'badge', label: 'Etiqueta de arriba' },
  { name: 'headline', label: 'Texto principal' },
  { name: 'subhead', label: 'Texto destacado (azul)' }
];

const CTA_FIELDS = [
  { name: 'ctaKicker', label: 'Antetítulo' },
  { name: 'ctaTitle', label: 'Título' },
  { name: 'ctaText', label: 'Texto', multiline: true },
  { name: 'ctaButton', label: 'Botón' }
];

/** Lo guardado → lo que se edita (las tarjetas siempre como lista). */
function toForm(config) {
  const clean = C.sanitize(config);
  return { ...clean, cards: clean.cards || C.defaultCards() };
}

const sameCards = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/** Lo que se edita → lo que se guarda (las tarjetas de fábrica sin tocar no se guardan). */
function toConfig(form) {
  const clean = C.sanitize(form);
  return { ...clean, cards: sameCards(clean.cards, C.defaultCards()) ? null : clean.cards };
}

const factoryCard = (card) => C.CARDS.find((c) => c.id === card.id) || null;

// Un 404 aquí no es «no hay nada guardado» (eso es config: null): es que el
// Worker que ha contestado no tiene todavía esta pantalla, es decir, no es el
// del código actual. Pasa con el Worker de producción sin desplegar y cuando
// el backoffice en local, al no encontrar su Worker local, se va solo al de
// producción (lib/api.js). Se dice cuál ha contestado para que se vea.
function explain(error, doing) {
  const text = String(error?.message || '');
  if (!/^404\b/.test(text)) return `${doing} (${text || 'error'}).`;
  const host = getApiBase().replace(/^https?:\/\//, '');
  return `${doing}: el Worker que ha contestado (${host}) no tiene todavía la pantalla de lanzamiento. Hay que desplegarlo; ` +
    'si estás probando en local, comprueba que tu Worker local está arrancado y recarga la página.';
}

function when(ms) {
  return ms ? new Date(ms).toLocaleString('es-ES', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
}

function IconSvg({ name, className = 'h-6 w-6' }) {
  return <span className="text-primary" dangerouslySetInnerHTML={{ __html: LaunchIcons.svg(name, className) }} />;
}

// Etiqueta encima del campo (la «outside» de NextUI no reserva su hueco y se monta sobre lo de arriba).
function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-default-600">{label}</span>
      {children}
      {hint ? <span className="mt-1 block text-xs text-default-400">{hint}</span> : null}
    </label>
  );
}

function Section({ title, hint, children, action }) {
  return (
    <section className="space-y-3 rounded-xl border border-default-200 bg-content1 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-base font-semibold">{title}</h2>
          {hint ? <p className="text-sm text-default-500">{hint}</p> : null}
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
      {children}
    </section>
  );
}

export default function AgenciaLanzamiento() {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(null);         // { json, updatedAt, updatedBy }
  const [lang, setLang] = useState('es');
  const [shape, setShape] = useState('wide');
  const [dict, setDict] = useState({ es });         // textos de la app, para las pantallas de los lados
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);     // { type: 'ok' | 'error', text }
  const [replay, setReplay] = useState(0);
  const previewRef = useRef(null);
  const introPending = useRef(false);               // «Ver la animación» pulsado y aún sin pintar

  useEffect(() => {
    // La fuente de la app, para que la vista previa mida lo mismo.
    if (!document.querySelector(`link[href="${LAUNCH_FONT_URL}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = LAUNCH_FONT_URL;
      document.head.appendChild(link);
    }
  }, []);

  const apply = useCallback((data) => {
    const next = toForm(data.config);
    setForm(next);
    setSaved({ json: JSON.stringify(toConfig(next)), updatedAt: data.updatedAt || null, updatedBy: data.updatedBy || null });
  }, []);

  useEffect(() => {
    let alive = true;
    apiFetch(ENDPOINT, { cache: 'no-store' })
      .then((data) => { if (alive) apply(data); })
      .catch((e) => {
        if (!alive) return;
        apply({ config: null });
        setMessage({ type: 'error', text: `${explain(e, 'No se pudo leer lo guardado')} Mientras tanto se enseña lo de fábrica.` });
      });
    return () => { alive = false; };
  }, [apply]);

  // Los textos de la app en inglés solo se cargan si se mira la vista en inglés.
  useEffect(() => {
    if (lang !== 'en' || dict.en) return;
    let alive = true;
    import('../../../renderer/i18n/locales/en.json')
      .then((mod) => { if (alive) setDict((d) => ({ ...d, en: mod.default })); })
      .catch(() => { if (alive) setDict((d) => ({ ...d, en: {} })); });
    return () => { alive = false; };
  }, [lang, dict.en]);

  // ── Vista previa: la pantalla de la app, repintada con cada cambio ──
  useEffect(() => {
    const host = previewRef.current;
    if (!host || !form) return undefined;
    const strings = dict[lang] || {};
    const t = (key, fallback) => strings[key] || fallback;
    // Solo al pulsar «Ver la animación». Se apaga en el siguiente turno: en
    // desarrollo React monta el efecto dos veces seguidas.
    const intro = introPending.current;
    if (intro) setTimeout(() => { introPending.current = false; }, 0);
    const view = LaunchView.mount(host, C.resolve(form, lang, t), { t, lang, logoUrl: LAUNCH_LOGO_URL, intro });
    return () => view.destroy();
  }, [form, lang, dict, shape, replay]);

  const dirty = useMemo(() => !!form && !!saved && JSON.stringify(toConfig(form)) !== saved.json, [form, saved]);
  const factory = useMemo(() => !!form && JSON.stringify(toConfig(form)) === JSON.stringify(toConfig(toForm(null))), [form]);

  if (!form) return <div className="flex justify-center p-8"><Spinner /></div>;

  // ── Edición ──
  const setPair = (group, name, value) => setForm((f) => ({ ...f, [group]: { ...f[group], [name]: { ...f[group][name], [lang]: value } } }));
  const setDateText = (value) => setForm((f) => ({ ...f, dateText: { ...f.dateText, [lang]: value } }));
  const setCheck = (i, value) => setForm((f) => ({ ...f, checks: f.checks.map((c, j) => (j === i ? { ...c, [lang]: value } : c)) }));
  const editCard = (i, patch) => setForm((f) => ({ ...f, cards: f.cards.map((c, j) => (j === i ? { ...c, ...patch } : c)) }));
  const moveCard = (i, dir) => setForm((f) => {
    const j = i + dir;
    if (j < 0 || j >= f.cards.length) return f;
    const cards = f.cards.slice();
    [cards[i], cards[j]] = [cards[j], cards[i]];
    return { ...f, cards };
  });
  const removeCard = (i) => setForm((f) => ({ ...f, cards: f.cards.filter((_, j) => j !== i) }));
  const addCard = () => setForm((f) => ({ ...f, cards: [...f.cards, { id: '', icon: LaunchIcons.FALLBACK, title: { es: '', en: '' }, text: { es: '', en: '' }, back: { es: '', en: '' } }] }));

  // Una tarjeta propia sin título en ningún idioma no se puede guardar (el Worker la descartaría).
  const untitled = form.cards.findIndex((c) => !factoryCard(c) && !c.title.es.trim() && !c.title.en.trim());

  async function save(config) {
    setBusy(true);
    setMessage(null);
    try {
      const data = await apiFetch(ENDPOINT, { method: 'PUT', body: { config } });
      apply(data);
      setMessage({ type: 'ok', text: data.config ? 'Guardado. La app lo enseña en unos 5 minutos.' : 'Guardado: la pantalla vuelve a ser la de fábrica.' });
    } catch (e) {
      setMessage({ type: 'error', text: explain(e, 'No se pudo guardar') });
    } finally {
      setBusy(false);
    }
  }

  function onSave() {
    if (untitled >= 0) {
      setMessage({ type: 'error', text: `La tarjeta ${untitled + 1} no tiene título. Escríbelo o quítala.` });
      return;
    }
    save(toConfig(form));
  }

  function onReset() {
    if (!window.confirm('¿Volver a la pantalla de fábrica? Se pierden la fecha, los textos y las tarjetas cambiados aquí.')) return;
    save(null);
  }

  const model = C.resolve(form, lang);
  const def = (d) => (lang === 'es' ? d.es : d.en);
  const shapeNow = SHAPES.find((s) => s.id === shape);
  const tone = { ok: 'text-success', error: 'text-danger' };
  const field = { size: 'sm', variant: 'bordered', isReadOnly: !canWrite };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 max-w-3xl">
          <button className="text-sm text-default-500 hover:underline" onClick={() => navigate('/agencias')}>← Agencias</button>
          <h1 className="text-2xl font-semibold">Lanzamiento de Agencias</h1>
          <p className="text-sm text-default-500">
            La pantalla que ve en la pestaña Agencias de la app todo el que no es ADMIN, hasta que Agencias se abra. Un campo vacío
            sale con su texto de fábrica (el que se ve en gris), traducido a los 14 idiomas de la app; lo que escribas aquí sale tal
            cual en español y, para el resto de idiomas, en inglés.
          </p>
          {saved?.updatedAt ? <p className="text-xs text-default-400">Último cambio: {when(saved.updatedAt)}</p> : null}
        </div>
        <div className="flex items-center gap-1 rounded-lg border border-default-200 p-1" role="group" aria-label="Idioma que editas y ves">
          {LANGS.map((l) => (
            <Button key={l.id} size="sm" variant={lang === l.id ? 'solid' : 'light'} color={lang === l.id ? 'primary' : 'default'} onPress={() => setLang(l.id)}>
              {l.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="grid items-start gap-4 xl:grid-cols-[minmax(360px,460px)_minmax(0,1fr)]">
        {/* ── Formulario ── */}
        <div className="order-2 space-y-4 xl:order-1">
          <Section title="Fecha" hint="Sale con el día y el mes en el idioma de cada usuario.">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Fecha de lanzamiento">
                <Input
                  {...field} type="date" aria-label="Fecha de lanzamiento"
                  value={form.date || C.DEFAULT_DATE} onValueChange={(date) => setForm((f) => ({ ...f, date }))}
                />
              </Field>
              <Field label="En su lugar, un texto" hint="Opcional. Por ejemplo, «Muy pronto».">
                <Input
                  {...field} aria-label="Texto en lugar de la fecha" maxLength={L.dateText}
                  placeholder={C.formatDate(form.date, lang)} value={form.dateText[lang]} onValueChange={setDateText}
                />
              </Field>
            </div>
          </Section>

          <Section title="Textos">
            {TEXT_FIELDS.map(({ name, label }) => (
              <Field key={name} label={label}>
                <Input
                  {...field} aria-label={label} maxLength={L[C.TEXTS[name].limit]}
                  placeholder={def(C.TEXTS[name])} value={form.texts[name][lang]} onValueChange={(v) => setPair('texts', name, v)}
                />
              </Field>
            ))}
          </Section>

          <Section
            title={`Tarjetas (${form.cards.length} de ${C.MAX_CARDS})`}
            hint={`Hasta ${C.ROW_MAX} van en una fila; con más, se reparten en dos. Cada una lleva su título, su texto y, debajo, el texto más largo que sale al girarla con el ratón encima (pruébalo en la vista previa).`}
            action={canWrite && !sameCards(C.sanitize(form).cards, C.defaultCards())
              ? <Button size="sm" variant="light" onPress={() => setForm((f) => ({ ...f, cards: C.defaultCards() }))}>Las de fábrica</Button>
              : null}
          >
            <ol className="space-y-2">
              {form.cards.map((card, i) => {
                const base = factoryCard(card);
                return (
                  <li key={i} className={`space-y-2 rounded-lg border p-3 ${untitled === i ? 'border-danger' : 'border-default-200'}`}>
                    <div className="flex items-center gap-2">
                      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-default-100"><IconSvg name={card.icon} /></span>
                      <select
                        aria-label={`Icono de la tarjeta ${i + 1}`} disabled={!canWrite} value={LaunchIcons.has(card.icon) ? card.icon : LaunchIcons.FALLBACK}
                        onChange={(ev) => editCard(i, { icon: ev.target.value })}
                        className="h-9 min-w-0 flex-1 rounded-md border border-default-200 bg-content1 px-2 text-sm"
                      >
                        {Object.entries(LaunchIcons.ICONS).map(([key, icon]) => <option key={key} value={key}>{icon.label}</option>)}
                      </select>
                      {canWrite ? (
                        <div className="flex shrink-0 gap-1">
                          <Button size="sm" variant="light" isIconOnly aria-label="Subir" isDisabled={i === 0} onPress={() => moveCard(i, -1)}>↑</Button>
                          <Button size="sm" variant="light" isIconOnly aria-label="Bajar" isDisabled={i === form.cards.length - 1} onPress={() => moveCard(i, 1)}>↓</Button>
                          <Button size="sm" variant="light" color="danger" isIconOnly aria-label="Quitar" onPress={() => removeCard(i)}>✕</Button>
                        </div>
                      ) : null}
                    </div>
                    <div className="grid gap-2 sm:grid-cols-2">
                      <Input
                        {...field} aria-label={`Título de la tarjeta ${i + 1}`} maxLength={L.cardTitle}
                        placeholder={base ? def(base)[0] : 'Título'} value={card.title[lang]}
                        onValueChange={(v) => editCard(i, { title: { ...card.title, [lang]: v } })}
                      />
                      <Input
                        {...field} aria-label={`Texto de la tarjeta ${i + 1}`} maxLength={L.cardText}
                        placeholder={base ? def(base)[1] : 'Texto de debajo'} value={card.text[lang]}
                        onValueChange={(v) => editCard(i, { text: { ...card.text, [lang]: v } })}
                      />
                    </div>
                    <Textarea
                      {...field} minRows={2} aria-label={`Texto al girar la tarjeta ${i + 1}`} maxLength={L.cardBack}
                      placeholder={base ? def(base)[2] : 'Texto al girarla (opcional): sin él, la tarjeta no se gira'} value={card.back[lang]}
                      onValueChange={(v) => editCard(i, { back: { ...card.back, [lang]: v } })}
                    />
                    {!base ? <p className="text-xs text-default-400">Tarjeta nueva: no tiene traducción de fábrica, escríbela también en {lang === 'es' ? 'inglés' : 'español'}.</p> : null}
                  </li>
                );
              })}
              {!form.cards.length ? <li className="text-sm text-default-500">Sin tarjetas: la pantalla sale sin ellas.</li> : null}
            </ol>
            {canWrite ? <Button size="sm" variant="flat" isDisabled={form.cards.length >= C.MAX_CARDS} onPress={addCard}>Añadir tarjeta</Button> : null}
          </Section>

          <Section
            title="Bloque «Agenda tu llamada»"
            hint="El botón abre el formulario de alta de agencia (el mismo de «¿Tu agencia no aparece?»): llega a Solicitudes y la agencia recibe el correo para reservar la reunión."
            action={<Switch size="sm" isSelected={!form.ctaHidden} isDisabled={!canWrite} onValueChange={(on) => setForm((f) => ({ ...f, ctaHidden: !on }))} aria-label="Mostrar el bloque" />}
          >
            {form.ctaHidden ? <p className="text-sm text-default-500">Oculto: la pantalla termina en las tarjetas.</p> : (
              <>
                {CTA_FIELDS.map(({ name, label, multiline }) => {
                  const props = {
                    ...field, 'aria-label': label, maxLength: L[C.TEXTS[name].limit],
                    placeholder: def(C.TEXTS[name]), value: form.texts[name][lang], onValueChange: (v) => setPair('texts', name, v)
                  };
                  return <Field key={name} label={label}>{multiline ? <Textarea minRows={2} {...props} /> : <Input {...props} />}</Field>;
                })}
                <Field label="Las tres ventajas de debajo del botón">
                  <div className="grid gap-2 sm:grid-cols-3">
                    {form.checks.map((check, i) => (
                      <Input
                        key={i} {...field} aria-label={`Ventaja ${i + 1}`} maxLength={L.check}
                        placeholder={def(C.CHECK_TEXTS[i])} value={check[lang]} onValueChange={(v) => setCheck(i, v)}
                      />
                    ))}
                  </div>
                </Field>
              </>
            )}
          </Section>

          <div className="sticky bottom-0 z-10 flex flex-wrap items-center gap-3 rounded-xl border border-default-200 bg-content1 p-3">
            {canWrite ? (
              <>
                <Button color="primary" isLoading={busy} isDisabled={!dirty} onPress={onSave}>Guardar</Button>
                <Button variant="light" isDisabled={busy || !dirty} onPress={() => { setForm(toForm(JSON.parse(saved.json))); setMessage(null); }}>Descartar cambios</Button>
                <Button variant="light" color="danger" isDisabled={busy || (factory && !dirty)} onPress={onReset}>Volver a la de fábrica</Button>
              </>
            ) : <span className="text-sm text-default-500">Solo lectura.</span>}
            {message ? <p role="status" className={`text-sm ${tone[message.type]}`}>{message.text}</p> : (dirty ? <p className="text-sm text-warning">Cambios sin guardar.</p> : null)}
          </div>
        </div>

        {/* ── Vista previa ── */}
        <div className="order-1 space-y-2 xl:sticky xl:top-0 xl:order-2">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="mr-auto text-base font-semibold">Así se ve en la app</h2>
            <div className="flex items-center gap-1 rounded-lg border border-default-200 p-1" role="group" aria-label="Forma de la ventana">
              {SHAPES.map((s) => (
                <Button key={s.id} size="sm" variant={shape === s.id ? 'flat' : 'light'} onPress={() => setShape(s.id)}>{s.label}</Button>
              ))}
            </div>
            <Button size="sm" variant="flat" onPress={() => { introPending.current = true; setReplay((n) => n + 1); }}>Ver la animación</Button>
          </div>
          <div className="flex justify-center overflow-hidden rounded-xl border border-default-200 bg-black">
            <div ref={previewRef} style={{ aspectRatio: shapeNow.ratio, width: shape === 'narrow' ? 'min(100%, 620px)' : '100%' }} />
          </div>
          <p className="text-xs text-default-400">
            {shapeNow.hint} {model.cards.length ? `${model.cards.length} tarjetas.` : 'Sin tarjetas.'} Las dos pantallas de los lados son decorado con datos de muestra.
          </p>
        </div>
      </div>
    </div>
  );
}
